import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

// Load the relativeTime plugin
dayjs.extend(relativeTime);

import {
  BASEURL,
  childNotAllowed,
  pleaseLoginToPerformThis,
} from "../../../constant/constant";
import { useSelector } from "react-redux";

import Loader from "../../loader/Loader";
import { apiCall } from "../../../API/apiCall";
import { commentSchema } from "../../../schema/schema";
import { BaseModal } from "../../Modal/BaseModal";
import DeleteModal from "../DeleteModal";

import "./comments.css";

const Comments = ({
  userType,
  blogId,
  comments,
  setComments,
  isLoadedComment,
  setIsLoadedComment,
  page,
  setPage,
  pageLoaded,
  setPageLoaded,
  hasMoreComments,
  setHasMoreComments,
  setTotalCommets,
}) => {
  const { user } = useSelector((st) => st.user);

  const [comment, setComment] = useState("");
  const [mode, setMode] = useState(null);
  const [commentToEdit, setCommentToEdit] = useState(null);
  const [isLoader, setIsLoader] = useState(true);
  const [isLoaderMain, setIsLoaderMain] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [commentsFetched, setCommentsFetched] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState("");

  const loaderRef = useRef(null);

  const [formError, setFormError] = useState({});

  const isFetchingRef = useRef(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Function to fetch comments
    const fetchComments = async () => {
      // Prevent fetching if comments are already loaded
      if (isLoadedComment && comments.length > 0 && page <= pageLoaded) {
        setCommentsFetched(true);
        return;
      }

      if (!hasMoreComments || isFetchingRef.current) return; // Prevent multiple fetches
      isFetchingRef.current = true;

      setIsLoader(true);
      try {
        const filter =
          userType !== "admin"
            ? false
            : filterType === "disabled"
            ? true
            : undefined;

        const response = await apiCall({
          url: `/blog/comments/${blogId}?page=${page}`,
          method: "GET",
          params: {
            limit: 10,
            isDisabled: filter,
          },
        });

        if (response.data && response.data.data) {
          // Set loaded state only if we successfully fetch comments
          setIsLoadedComment(true);
          setPageLoaded(response?.pagination?.currentPage);
          if (response.data.data.length === 0) {
            setHasMoreComments(false);
          } else {
            setComments((prevComments) => [
              ...prevComments,
              ...response.data.data,
            ]);

            setCommentsFetched(true);
            if (!response?.pagination?.nextPage) {
              setHasMoreComments(false);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        isFetchingRef.current = false; // Reset fetching state
        setIsLoader(false);
      }
    };

    fetchComments();
  }, [page, filterType]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMoreComments && commentsFetched) {
        setPage((prevPage) => prevPage + 1);
        setCommentsFetched(false); // Reset comments fetched state
      }
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMoreComments, commentsFetched]);

  // Method that will handle comment add or edit functionality.
  const handleAddOrEditComment = async () => {
    try {
      const error = commentSchema({
        text: comment,
      });

      if (Object.keys(error).length !== 0) {
        setFormError(error);
        return;
      }

      if (!user) {
        toast.error(pleaseLoginToPerformThis);
        setComment("");
        setFormError({});
        return;
      } else if (user.Username && user.Username.length !== 0) {
        toast.error(childNotAllowed);
        setComment("");
        setFormError({});
        return;
      }

      setIsLoaderMain(true);

      let response;
      // If blog execute when user update the comment otherwiese else block will execute for add new comment.
      if (mode !== null) {
        response = await apiCall({
          url: `/blog/comments/${commentToEdit._id}`,
          method: "PATCH",
          data: {
            text: comment,
            isDisabled: false,
          },
        });

        if (response.success) {
          const updatedComments = comments.map((comm) =>
            comm._id === commentToEdit._id ? { ...comm, text: comment } : comm
          );

          setComments(updatedComments);

          setComment("");
          setMode(null);
        }
      } else {
        // Execute when user add new comment.
        response = await apiCall({
          url: `/blog/comments/${blogId}`,
          method: "POST",
          data: {
            text: comment,
          },
        });

        if (response.status === "success") {
          setComments((prevComments) => [
            {
              author: {
                Fname: user?.Fname,
                Lname: user?.Lname,
                profileUrl: user?.profileUrl,
                _id: user?._id,
              },
              text: comment,
              _id: response?.data?.data?._id,
            },
            ...prevComments,
          ]);
          setComment("");
          setTotalCommets((prev) => prev * 1 + 1);
          setFormError({});
        }
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoaderMain(false);
    }
  };

  // Function to handle filter change.
  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
    setComments([]);
    setPage(1);
    setHasMoreComments(true);
    setCommentsFetched(false);
  };

  // Method that will handle comment disable functionality.
  const handleCommentDisable = async (commentToUpdate) => {
    setIsLoader(true);

    try {
      const response = await apiCall({
        url: `/blog/comments/disable/${commentToUpdate?._id}`,
        method: "PATCH",
        data: {
          isDisabled: !commentToUpdate?.isDisabled,
          blogId: blogId,
        },
      });
      if (filterType === "disabled") {
        setComments((prevComments) =>
          prevComments.filter(
            (comment) => comment?._id !== commentToUpdate?._id
          )
        );
      } else {
        setComments((prevComments) =>
          prevComments.map((comment) =>
            comment._id === commentToUpdate._id
              ? { ...comment, isDisabled: !comment.isDisabled }
              : comment
          )
        );
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoader(false);
    }
  };

  // Method that will set data for the comment that user will going to update.
  const handleEditComment = (comment, mode) => {
    setMode(mode);
    setFormError({});
    setComment(comment.text);
    setCommentToEdit(comment);
    textareaRef?.current?.focus();
  };

  // Method that will open or close the modal for comment delete confirmation when user want to delete a comment.
  const handleCloseDeleteModal = (commentId) => {
    setDeleteModal(!deleteModal);
    setCommentIdToDelete(commentId);
  };

  // Method that will handle delete comment functionality.
  const handleDeleteComment = async () => {
    setIsLoader(true);
    try {
      await apiCall({
        url: `blog/comments/${commentIdToDelete}`,
        method: "DELETE",
      });

      setComments((prevComments) =>
        prevComments.filter((comment) => comment?._id !== commentIdToDelete)
      );

      setTotalCommets((prev) => prev * 1 - 1);

      toast.success("Deleted Successfully.");
      setDeleteModal(false);
    } catch (err) {
      setIsLoader(false);
      console.log("Err : ", err);
      toast(err.message);
    }
  };

  // Methd that will clear the data from the state, that is going to upadate by the user if user don't want to update the selected comment then this method will clear all the data of the selected comment.
  const handleCancel = () => {
    setComment("");
    setMode(null);
    setCommentToEdit(comment);
  };

  return (
    <div>
      <Box
        sx={{ padding: 2, border: "1px solid #d9d9d9", borderRadius: "8px" }}
      >
        {userType !== "admin" && (
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="h6">Add a comment...</Typography>
            <textarea
              ref={textareaRef}
              value={comment}
              className={`form-control mb-4 ${
                formError.text ? "is-invalid" : ""
              }`}
              onChange={(e) => setComment(e.target.value)}
              style={{
                width: "100%",
                height: "100px",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
            {formError.text && (
              <Typography color="error">{formError.text}</Typography>
            )}

            <Button
              variant="contained"
              disabled={isLoaderMain}
              onClick={handleAddOrEditComment}
              sx={{ marginRight: "6px" }}
            >
              {mode === "edit" ? "Update" : "Send"}{" "}
              {isLoaderMain && <CircularProgress size={24} />}
            </Button>
            {mode === "edit" && (
              <Button
                onClick={handleCancel}
                variant="contained"
                sx={{ background: "#f9b6b6", color: "#af0000" }}
              >
                Cancel
              </Button>
            )}
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            marginBottom: 2,
            borderBottom: "1px solid rgb(150, 147, 147)",
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Comments
          </Typography>
          {userType === "admin" && (
            <select
              onChange={handleFilterChange}
              style={{ marginLeft: "16px" }}
            >
              <option value="All">All</option>
              <option value="disabled">Disabled</option>
            </select>
          )}
        </Box>
        <Box
          className="commentslist-content"
          sx={{
            maxHeight: "400px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <List>
            {comments?.length === 0 && !isLoader ? (
              <Typography sx={{ textAlign: "center" }}>
                No comments found for the selected filter.
              </Typography>
            ) : (
              comments?.map((comment, index) => (
                <ListItem
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: 2,
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: 2,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      alt="Author"
                      src={`${BASEURL}/${comment?.author?.profileUrl}`}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box
                          flexGrow={1}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{ marginRight: 1 }}
                          >
                            {`@${comment?.author?.Fname} ${comment?.author?.Lname}`}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            component="div"
                            sx={{ marginLeft: 1 }}
                          >
                            {` • ${dayjs(comment?.createdAt).fromNow()}`}
                          </Typography>
                        </Box>
                        {comment?.author?._id === user?._id && (
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{ marginLeft: 2 }}
                          >
                            <Button
                              variant="outlined"
                              onClick={() => handleEditComment(comment, "edit")}
                              sx={{ marginLeft: 1 }}
                            >
                              <Tooltip title="Edit comment">
                                <EditIcon />
                              </Tooltip>
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() =>
                                handleCloseDeleteModal(comment._id)
                              }
                              sx={{ marginLeft: 1 }}
                            >
                              <Tooltip title="Delete comment">
                                <DeleteIcon />
                              </Tooltip>
                            </Button>
                          </Box>
                        )}
                        {userType === "admin" && (
                          <Tooltip title="Toggle visibility">
                            <i
                              className={
                                comment?.isDisabled
                                  ? "fa-regular fa-eye-slash"
                                  : "fa-regular fa-eye"
                              }
                              onClick={() => handleCommentDisable(comment)}
                              style={{ cursor: "pointer", marginLeft: "8px" }}
                            ></i>
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                        }}
                        component="div"
                      >
                        {comment?.text}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            )}
            {comments?.length !== 0 && !hasMoreComments && (
              <Typography sx={{ textAlign: "center" }}>
                You've reached the end of the comments.
              </Typography>
            )}
            {hasMoreComments && (
              <li ref={loaderRef} className="commntsloader">
                {isLoader && <Loader />}
              </li>
            )}
          </List>
        </Box>
      </Box>

      <BaseModal
        classname="base_modal_mui"
        open={deleteModal}
        handleClose={handleCloseDeleteModal}
        maxWidth={600}
      >
        <DeleteModal
          handleSubmit={handleDeleteComment}
          handleCloseModal={handleCloseDeleteModal}
          modalName={"Comment"}
          message="Delete your comment permanently?"
        />
      </BaseModal>
    </div>
  );
};

export default Comments;
